import {
	COUNTRY_EXCEPTIONS,
	LANG_COUNTRY_MAPPING,
	WEB_LOCALES,
	WEB_LOCALES_EMOJIS,
	WebLocales,
} from '@/constants/web-locales.constant';
import { WebLocale } from '@/enums/web-locale';
import { useI18n } from '@/helpers/composables/useI18n';
import { FallbackLocale, getActiveLocales, Locale } from '@/helpers/locale-helper';
import { useFilterStore } from '@/stores/pinia/useFilterStore';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { defineStore } from 'pinia';
import { computed, ref, shallowRef } from 'vue';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { fetchAppTranslations, fetchSportsTranslations } from '@/helpers/language-helpers';

/** If all else fails, fallback to English. */
const DEFAULT_LANGUAGE = 'en';

export const TTB_LANGUAGES = ['ja', 'ko', 'zh'];

export const useLanguageStore = defineStore('language', () => {
	const filterStore = useFilterStore();

	const i18n = useI18n();

	const webLocale = ref(WebLocale.us);

	// Do not set this value directly, use `setLanguage`
	const overriddenLanguage = ref('');

	const detectedWebLocale = ref<WebLocale | null>(null);
	const locales = shallowRef<Locale[]>([]);
	const localeMessage = shallowRef<any>({});
	const audioLanguage = ref('');

	const language = computed(() => {
		// If the language is supported in LANG_COUNTRY_MAPPING return that
		if (LANG_COUNTRY_MAPPING[overriddenLanguage.value ?? ''] != null) {
			return overriddenLanguage.value;
		}

		// Otherwise try and get the language from the weblocale
		if (WEB_LOCALES[webLocale.value] != null) {
			return WEB_LOCALES[webLocale.value].substring(0, 2).toLowerCase();
		}

		// if all else fails, default to english
		return DEFAULT_LANGUAGE;
	});

	const isTTBLanguage = computed(() => TTB_LANGUAGES.includes(language.value));

	const country = computed(() => {
		const country = WEB_LOCALES[webLocale.value];
		if (country) {
			return country.substring(country.length - 2).toUpperCase();
		}

		return 'US';
	});

	const countryEmoji = computed(() =>
		webLocale.value && WEB_LOCALES_EMOJIS[webLocale.value] ? WEB_LOCALES_EMOJIS[webLocale.value] : null
	);

	const locale = computed(() =>
		webLocale.value && WEB_LOCALES[webLocale.value] ? WEB_LOCALES[webLocale.value] : 'en_US'
	);

	const localeObject = computed(
		() => locales.value.filter(locale => locale.webLocale === webLocale.value).find(Boolean) ?? FallbackLocale
	);

	const overriddenLocale = computed(() =>
		overriddenLanguage.value ? `${overriddenLanguage.value}_${country.value}` : locale.value
	);

	const activeLocalesList = computed(() =>
		locales.value.length
			? (locales.value.reduce((arr: { [key: string]: string }, entry) => {
					const { webLocale, full_locale } = entry;
					arr[webLocale] = full_locale;
					return arr;
			  }, {}) as WebLocales)
			: WEB_LOCALES
	);

	async function initLanguage() {
		const userStore = useUserStore();

		// Get language from localStorage or detect browser language
		let language = userStore.persistedOverriddenLanguage ?? getLanguageFromLocale(detectedWebLocale.value!);

		// Hard fallback
		if (!language) {
			language = DEFAULT_LANGUAGE;

			captureMessageForSentry(`[Language] not able to detect language, hard fallback to ${DEFAULT_LANGUAGE}`, {
				where: '[main.common.ts] initLanguage Failed to init',
			});
		}

		if (Object.keys(localeMessage.value).length === 0) {
			await fetchTranslation(language);
		}

		setLanguage(language);
	}

	async function changeWebLocale(newWebLocale: WebLocale) {
		if (newWebLocale === webLocale.value) return;

		if (!filterStore.getWebLocale(newWebLocale)) {
			// init filter here before setting webLocale for reactivity reason (webLocale is watched everywhere)
			filterStore.initFiltersPerLocale(newWebLocale);
		}

		webLocale.value = newWebLocale;

		// main.ssr.ts already loads user settings on the server,
		// this prevents an expensive double call.
		// changeWebLocale would now only be called on client when
		// the user changes country.
		if (process.client) {
			const userStore = useUserStore();
			await userStore.loadSettings();
		}
	}

	async function setLanguageWhenUserDidNot(language: string) {
		// Don't override if the user explicitly chose a language
		if (useUserStore().persistedOverriddenLanguage != null) return;
		// Don't override if the non-default language is already active
		if (overriddenLanguage.value && overriddenLanguage.value !== DEFAULT_LANGUAGE) return;

		await fetchTranslation(language);
		setLanguage(language);
	}

	function setLanguage(_language: string) {
		if (!_language) return false;

		// Safety check that the language is valid
		const language = LANG_COUNTRY_MAPPING[_language] != null ? _language : DEFAULT_LANGUAGE;

		const localeTranslations = global.VUE_APP_TESTS_E2E ? {} : localeMessage.value;

		i18n.setLocaleMessage(language, localeTranslations);
		i18n.locale.value = language;

		overriddenLanguage.value = language;
	}

	function setPreferredAudioLanguage({ language }: { language: string }) {
		audioLanguage.value = language;
	}

	async function fetchTranslation(language: string) {
		const routingStore = useRoutingStore();

		const locale = country.value;

		const fetchPromises = [fetchAppTranslations(language, locale)];

		// Fetch sports translations too if on a sports or home route
		const sportRoutePrefixes = ['app.sports', 'app.home'];
		if (sportRoutePrefixes.some(prefix => routingStore.activeRoute?.name?.startsWith(prefix))) {
			fetchPromises.push(fetchSportsTranslations(language, locale));
		}

		const [jwApp, jwSports = {}] = await Promise.all(fetchPromises);

		localeMessage.value = Object.freeze({ ...jwApp, ...jwSports });

		i18n.setLocaleMessage(language, localeMessage.value);
	}

	async function fetchSportsTranslation() {
		// check if the sports translations are already loaded
		const SPORTS_TRANSLATION_PREFIX = 'SPORTS_WEBAPP_';

		if (Object.keys(localeMessage.value).some(key => key.startsWith(SPORTS_TRANSLATION_PREFIX))) return;
		const locale = webLocale.value ?? country.value;

		const jwSports = await fetchSportsTranslations(language.value, locale);

		localeMessage.value = Object.freeze({ ...localeMessage.value, ...jwSports });

		i18n.setLocaleMessage(language.value, localeMessage.value);
	}

	async function fetchLocales() {
		if (locales.value.length > 0) return;

		const weblocales = await getActiveLocales();

		if (weblocales?.length > 0) {
			weblocales.forEach(w => Object.freeze(w));
			locales.value = weblocales;
		}
	}

	function setDetectedWebLocale(locale: string) {
		const webLocale = COUNTRY_EXCEPTIONS[locale] ? COUNTRY_EXCEPTIONS[locale] : locale;
		detectedWebLocale.value = webLocale as WebLocale;
		if (WEB_LOCALES[webLocale as WebLocale]) {
			changeWebLocale(webLocale as WebLocale);
		}
	}

	async function setWebLocale(newWebLocale: WebLocale) {
		await changeWebLocale(newWebLocale);
		const userStore = useUserStore();
		userStore.setPersistedWebLocale(newWebLocale);
	}

	return {
		webLocale,
		overriddenLanguage,
		detectedWebLocale,
		locales,
		localeMessage,
		audioLanguage,

		language,
		isTTBLanguage,
		country,
		countryEmoji,
		locale,
		localeObject,
		overriddenLocale,
		activeLocalesList,
		initLanguage,
		changeWebLocale,
		setLanguageWhenUserDidNot,
		fetchTranslation,
		setLanguage,
		setPreferredAudioLanguage,
		fetchSportsTranslation,
		fetchLocales,
		setDetectedWebLocale,
		setWebLocale,
	};
});

/**
 * Tries to get the matching language given a weblocale.
 * Returns `null` if one isn't found.
 */
function getLanguageFromLocale(webLocale: WebLocale): string | null {
	const locale = WEB_LOCALES[webLocale] ?? '';
	const language = locale.substring(0, 2);

	return LANG_COUNTRY_MAPPING[language] != null ? language : null;
}
